import {
  SAFETY_DAYS_SEED,
  CALLBACKS_SEED,
  REVENUE_TODAY_SEED,
  THROUGHPUT_ANCHOR,
  REVENUE_PER_HOUR_ANCHOR,
  WIP_AGE_P90_ANCHOR,
  SKU_STOCK_LOW_SEED,
  TECHNICIANS_ON_SHIFT_ANCHOR,
  INSURANCE_SPLIT_ANCHOR,
} from './constants';

const TICK_MS = 2000;
const BUFFER = 60;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const walk = (prev, drift, lo, hi) => clamp(prev + (Math.random() * 2 - 1) * drift, lo, hi);

const initialSnapshot = () => ({
  tick: 0,
  throughputPerMin: [THROUGHPUT_ANCHOR],
  revenuePerHour: [REVENUE_PER_HOUR_ANCHOR],
  revenueToday: REVENUE_TODAY_SEED,
  wipAgeP90Mins: [WIP_AGE_P90_ANCHOR],
  skuStockLow: SKU_STOCK_LOW_SEED,
  techniciansOnShift: TECHNICIANS_ON_SHIFT_ANCHOR,
  safetyDaysWithoutIncident: SAFETY_DAYS_SEED,
  callbacksToday: CALLBACKS_SEED,
  insuranceSplitPct: INSURANCE_SPLIT_ANCHOR,
  jobsDoneToday: 42,
  avgTicketR: 2450,
});

let snapshot = initialSnapshot();
const listeners = new Set();
let intervalHandle = null;

const push = (arr, v) => {
  const next = arr.concat(v);
  if (next.length > BUFFER) next.shift();
  return next;
};

const tick = () => {
  const prevThru = snapshot.throughputPerMin[snapshot.throughputPerMin.length - 1];
  const prevRev = snapshot.revenuePerHour[snapshot.revenuePerHour.length - 1];
  const prevWip = snapshot.wipAgeP90Mins[snapshot.wipAgeP90Mins.length - 1];

  const newThru = Math.round(walk(prevThru, 0.4, 3, 9) * 10) / 10;
  const newRev = Math.round(walk(prevRev, prevRev * 0.05, 14000, 24000));
  const newWip = Math.round(walk(prevWip, 2, 8, 32));

  // revenueToday ticks forward by revenuePerHour * (TICK_MS / 3_600_000)
  const revenueToday = Math.round(snapshot.revenueToday + newRev * (TICK_MS / 3600000));

  // slow integer walks
  const every = (n) => snapshot.tick % n === 0;
  const maybeStep = (v, lo, hi, n) =>
    every(n) ? clamp(v + (Math.random() < 0.5 ? -1 : 1), lo, hi) : v;

  snapshot = {
    tick: snapshot.tick + 1,
    throughputPerMin: push(snapshot.throughputPerMin, newThru),
    revenuePerHour: push(snapshot.revenuePerHour, newRev),
    revenueToday,
    wipAgeP90Mins: push(snapshot.wipAgeP90Mins, newWip),
    skuStockLow: maybeStep(snapshot.skuStockLow, 0, 9, 10),
    techniciansOnShift: maybeStep(snapshot.techniciansOnShift, 8, 14, 15),
    safetyDaysWithoutIncident: snapshot.safetyDaysWithoutIncident,
    callbacksToday: maybeStep(snapshot.callbacksToday, 0, 12, 20),
    insuranceSplitPct: Math.round(clamp(snapshot.insuranceSplitPct + (Math.random() * 2 - 1), 55, 80)),
    jobsDoneToday: snapshot.jobsDoneToday + (every(5) ? 1 : 0),
    avgTicketR: Math.round(walk(snapshot.avgTicketR, 60, 1800, 3400)),
  };

  listeners.forEach((fn) => fn());
};

const ensureInterval = () => {
  if (intervalHandle === null && typeof window !== 'undefined') {
    intervalHandle = window.setInterval(tick, TICK_MS);
  }
};

export const liveFeed = {
  subscribe(fn) {
    listeners.add(fn);
    ensureInterval();
    return () => {
      listeners.delete(fn);
      if (listeners.size === 0 && intervalHandle !== null) {
        window.clearInterval(intervalHandle);
        intervalHandle = null;
      }
    };
  },
  getSnapshot() {
    return snapshot;
  },
  // exposed for tests / future manual control
  _tickForTesting: tick,
};
